import { useEffect, useState } from "react";
import { ProductTable } from "../../components/admin/ProductTable";
import { ProductForm } from "../../components/admin/ProductForm";
import { Button } from "../../components/Button";
import type { ProductWithImages } from "../../types";
import { usePageTitle } from "../../hooks/usePageTitle";

export function Products() {
  usePageTitle("Manage Products");
  const [products, setProducts] = useState<ProductWithImages[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductWithImages | undefined>();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch("/api/admin/products");
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleCreate = async (data: any) => {
    try {
      const response = await fetch("/api/admin/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        await fetchProducts();
        setShowForm(false);
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          // Show validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, message]) => `${field}: ${message}`)
            .join("\n");
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert(`Failed to create product: ${errorData.error || "Unknown error"}`);
        }
        throw new Error("Validation failed");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      throw error;
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingProduct) return;

    try {
      const response = await fetch(`/api/admin/products/${editingProduct.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        await fetchProducts();
        setShowForm(false);
        setEditingProduct(undefined);
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          // Show validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, message]) => `${field}: ${message}`)
            .join("\n");
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert(`Failed to update product: ${errorData.error || "Unknown error"}`);
        }
        throw new Error("Validation failed");
      }
    } catch (error) {
      console.error("Error updating product:", error);
      throw error;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`/api/admin/products/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        await fetchProducts();
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleEdit = (product: ProductWithImages) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Products</h1>
        {!showForm && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            Add New Product
          </Button>
        )}
      </div>

      {showForm ? (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold mb-4">
            {editingProduct ? "Edit Product" : "Add New Product"}
          </h2>
          <ProductForm
            product={editingProduct}
            onSubmit={editingProduct ? handleUpdate : handleCreate}
            onCancel={() => {
              setShowForm(false);
              setEditingProduct(undefined);
            }}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <ProductTable
            products={products}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      )}
    </div>
  );
}
